// The firm's own library one level down: what a test is trying to satisfy, and what has to be
// pulled off each item to tell.
//
// The real thing already half exists as versioned test templates in the Testing engine; this is a
// demo stand-in with the same shape.
import {ControlFrequency, SampleMethod, TestParameters} from "./model";

export type Criterion = {name: string; guidance: string};
export type Field = {name: string; type: string; guidance: string};

export type Procedure = {
  id: string;
  name: string;
  version: string;
  /** Which processes it is normally reached for, so a sensible default can be offered. */
  areas: string[];
  criteria: Criterion[];
  fields: Field[];
};

export const PROCEDURES: Procedure[] = [
  {
    id: "lib-three-way-match",
    name: "Three-way match",
    version: "v4",
    areas: ["Vendor Invoice"],
    criteria: [
      {name: "Quantity agrees", guidance: "The invoiced quantity equals the quantity on the goods receipt."},
      {name: "Price agrees", guidance: "The invoiced unit price equals the price on the purchase order."},
      {name: "Mismatch blocked", guidance: "Where either differs, the invoice was held rather than posted."},
    ],
    fields: [
      {name: "Invoice quantity", type: "Number", guidance: "From the invoice line."},
      {name: "Receipt quantity", type: "Number", guidance: "From the goods receipt."},
      {name: "Invoice price", type: "Number", guidance: "Unit price on the invoice line."},
      {name: "PO price", type: "Number", guidance: "Unit price on the matching purchase order line."},
      {name: "Posting status", type: "Text", guidance: "Posted, held or blocked."},
    ],
  },
  {
    id: "lib-vendor-approval",
    name: "Vendor master approval",
    version: "v2",
    areas: ["Vendor Invoice"],
    criteria: [
      {name: "Requested by an authorised requester", guidance: "The requester appears on the approved requester list."},
      {name: "Approved independently", guidance: "The approver is somebody other than the requester."},
      {name: "Applied as approved", guidance: "The details applied match those approved."},
    ],
    fields: [
      {name: "Requester", type: "Text", guidance: "Who raised the change."},
      {name: "Approver", type: "Text", guidance: "Who approved it."},
      {name: "Approved bank details", type: "Text", guidance: "As stated on the approval."},
      {name: "Applied bank details", type: "Text", guidance: "As held on the vendor master afterwards."},
    ],
  },
  {
    id: "lib-je-authorisation",
    name: "Journal authorisation",
    version: "v3",
    areas: ["Financial Controls"],
    criteria: [
      {name: "Approver recorded", guidance: "The journal carries a named approver."},
      {name: "Approver is not the preparer", guidance: "The two names differ."},
      {name: "Approved before posting", guidance: "The approval date is on or before the posting date."},
    ],
    fields: [
      {name: "Preparer", type: "Text", guidance: "Who raised the journal."},
      {name: "Approver", type: "Text", guidance: "Who approved it."},
      {name: "Approval date", type: "Date", guidance: "When approval was recorded."},
      {name: "Posting date", type: "Date", guidance: "When the journal hit the ledger."},
    ],
  },
  {
    id: "lib-access-review",
    name: "Periodic access review",
    version: "v3",
    areas: ["GITC Assessment"],
    criteria: [
      {name: "Performed on time", guidance: "The review falls within the required interval."},
      {name: "Reviewer independent", guidance: "The reviewer does not hold the access being reviewed."},
      {name: "Exceptions actioned", guidance: "Every exception raised was resolved and evidenced."},
    ],
    fields: [
      {name: "Review date", type: "Date", guidance: "When the review was signed off."},
      {name: "Reviewer", type: "Text", guidance: "Who performed it."},
      {name: "Exceptions raised", type: "Number", guidance: "Count from the review record."},
      {name: "Exceptions closed", type: "Number", guidance: "Count evidenced as resolved."},
    ],
  },
  {
    id: "lib-segregation",
    name: "Segregation of duties",
    version: "v2",
    areas: ["Order to Cash", "Vendor Invoice"],
    criteria: [
      {name: "Preparer and approver differ", guidance: "The two are different people."},
      {name: "No conflicting role", guidance: "Neither holds a role combination flagged as conflicting."},
      {name: "Conflicts resolved", guidance: "Any conflict identified was resolved before posting."},
    ],
    fields: [
      {name: "Preparer", type: "Text", guidance: "Who raised the transaction."},
      {name: "Approver", type: "Text", guidance: "Who released it."},
      {name: "Roles held", type: "Text", guidance: "System roles for both parties at the time."},
    ],
  },
  {
    id: "lib-reconciliation-review",
    name: "Reconciliation review",
    version: "v3",
    areas: ["Financial Controls", "Payroll"],
    criteria: [
      {name: "Prepared on time", guidance: "Prepared within the required interval of the period end."},
      {name: "Reviewed independently", guidance: "Reviewed by somebody other than the preparer."},
      {name: "Items cleared", guidance: "Reconciling items were cleared or explained."},
    ],
    fields: [
      {name: "Period end", type: "Date", guidance: "The period being reconciled."},
      {name: "Preparer", type: "Text", guidance: "Who prepared it."},
      {name: "Reviewer", type: "Text", guidance: "Who reviewed it."},
      {name: "Review date", type: "Date", guidance: "When the review was evidenced."},
      {name: "Open items", type: "Number", guidance: "Reconciling items left unexplained."},
    ],
  },
  {
    id: "lib-config-reperformance",
    name: "Configuration re-performance",
    version: "v2",
    areas: ["Order to Cash", "Vendor Invoice", "Payroll"],
    criteria: [
      {name: "Configuration matches the description", guidance: "The configured rule is the one the control claims."},
      {name: "Breach rejected", guidance: "A transaction breaching the rule was refused."},
      {name: "Compliant transaction accepted", guidance: "A transaction satisfying the rule went through."},
    ],
    fields: [
      {name: "Configured rule", type: "Text", guidance: "As read from the application."},
      {name: "Breach outcome", type: "Text", guidance: "What the application did with the breaching transaction."},
      {name: "Compliant outcome", type: "Text", guidance: "What it did with the compliant one."},
    ],
  },
];

export const procedureById = (id: string): Procedure | undefined => PROCEDURES.find(p => p.id === id);

export const procedureForArea = (area?: string): Procedure | undefined =>
  area ? PROCEDURES.find(p => p.areas.includes(area)) : undefined;

/**
 * Sample size by how often the control runs. Sizing belongs to the Testing engine rather than to
 * operating effectiveness; the plan only needs the number to say what it is proposing.
 */
export const FREQUENCY_SIZE: Record<ControlFrequency, number> = {
  daily: 25,
  weekly: 5,
  monthly: 2,
  quarterly: 2,
  annually: 1,
};

export const FREQUENCY_LABEL: Record<ControlFrequency, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annually: "Annually",
};

/**
 * Turn a procedure and a stated goal into something the testing engine can be handed. The goal is
 * what makes the parameters specific to this control — without it the library gives the same test
 * to everybody.
 */
export const generateParameters = (
  goal: string,
  {
    procedure,
    population,
    frequency,
    singleItem,
  }: {procedure?: Procedure; population: string; frequency: ControlFrequency; singleItem: boolean},
): TestParameters => {
  const method: SampleMethod = singleItem ? "single_item" : "random";
  const sampleSize = singleItem ? 1 : FREQUENCY_SIZE[frequency];

  if (!procedure) {
    // No library procedure: the goal is the only thing to test against, and it says so.
    return {
      population,
      method,
      sampleSize,
      criteria: [{name: "Goal met", guidance: goal}],
      fields: [{name: "Evidence of performance", type: "Text", guidance: "Whatever shows the goal was met."}],
    };
  }

  return {
    population,
    method,
    sampleSize,
    // The goal narrows the library's wording to this control; the criteria themselves come from
    // the procedure, which is what makes the result comparable across an audit.
    criteria: procedure.criteria.map(c => ({...c, guidance: `${c.guidance} In this test: ${goal}`})),
    fields: procedure.fields,
  };
};
